<template>
  <div
    v-if="options"
    class="combo-market-2"
  >
    <div class="combo-market-select">
      <ComboMarketSelect
        :list="options.types"
        :index="curIndex"
        @change="changeType"
      />

      <template v-for="(type, index) in options.types">
        <ComboMarketSelect
          v-show="index === curIndex"
          :key="`${type.label}_${index}`"
          :list="options.times[type.label]"
          :index="curTimeIndex"
          @change="changeTime"
        />
      </template>
    </div>

    <template v-for="(type, index) in options.types">
      <template v-for="(time, i) in options.times[type.label]">
        <template v-if="getType(`${type.label}-${time.label}`) !== 5">
          <Market1Specifier
            v-show="(index === curIndex && i === curTimeIndex && time.active) || time.visible"
            :key="`${type.label}_${index}_${time.label}`"
            :markets="comboMarketGroups.map[marketList[`${type.label}-${time.label}`]]"
            :event="event"
            @checkDeactive="checkDeactive"
          />
        </template>
        <template v-else>
          <Market5Normal
            v-show="(index === curIndex && i === curTimeIndex && time.active) || time.visible"
            :key="`${type.label}_${index}_${i}`"
            :markets="comboMarketGroups.map[marketList[`${type.label}-${time.label}`]]"
            :event="event"
            @checkDeactive="checkDeactive"
          />
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from '@vue/composition-api'
import { storeToRefs } from 'pinia'
import { useDetailStore } from '@ms/stores/matchDetail'
import { useMatchDetailStore } from 'wap/stores/matchDetail'
import ComboMarketSelect from './ComboMarketSelect'
import Market1Specifier from './Market1Specifier.vue'
import Market5Normal from './Market5Normal.vue'

export default {
  name: 'ComboMarket2',
  components: {
    ComboMarketSelect,
    Market1Specifier,
    Market5Normal
  },
  props: {
    group: {
      type: Object,
      default: () => ({})
    }
  },
  setup(props) {
    const { event } = storeToRefs(useMatchDetailStore())

    const { comboMarketGroups } = storeToRefs(useDetailStore())

    const curIndex = ref(0)
    const curTimeIndex = ref(0)
    const options = ref(null)

    const marketList = computed(() => {
      const { marketGroups, comboType, comboGroupName } = props.group
      const groups = marketGroups.filter((x) => x.markets.length)

      const list = {}
      groups.forEach((x) => {
        const { marketTypeDesc, markets } = x
        const { id } = markets[0]
        list[marketTypeDesc] = `${comboType}_${comboGroupName}_${id}`
      })

      return list
    })

    const changeType = (index) => {
      const { types, times } = options.value
      const curTimes = times[types[index].label]
      const isSame = curTimes[curTimeIndex.value] ? curTimes[curTimeIndex.value].active : false
      if (!isSame) {
        const i = curTimes.findIndex((x) => x.active)
        curTimeIndex.value = i
      }
      curIndex.value = index
    }

    const changeTime = (index) => {
      curTimeIndex.value = index
    }

    const getType = (key) => {
      const markets = comboMarketGroups.value.map[marketList.value[key]]
      return Array.isArray(markets) ? markets[0].presentationType : markets.presentationType
    }

    const getOptions = () => {
      const types = []
      const times = {}
      const { marketGroups } = props.group
      marketGroups.forEach((group) => {
        if (group.markets.length) {
          const typeDesc = group.marketTypeDesc.split('-')
          const i = types.findIndex((x) => x.label === typeDesc[0])
          if (i === -1) {
            types.push({ label: typeDesc[0], active: true })
          }
          if (!times[typeDesc[0]]) {
            times[typeDesc[0]] = [{ label: typeDesc[1], active: true, visible: true }]
          } else {
            times[typeDesc[0]].push({ label: typeDesc[1], active: true, visible: true })
          }
        }
      })
      return { types, times }
    }

    const checkDeactive = (status, option) => {
      const { types, times } = options.value
      const [typeLabel, timeLabel] = option

      // Update the active status of the selected time option
      const timeOptions = times[typeLabel]
      const selectedTimeIndex = timeOptions.findIndex(time => time.label === timeLabel)
      if (selectedTimeIndex !== -1) {
        timeOptions[selectedTimeIndex].active = status
      }

      // Find the first active time option if current one is deactivated
      if (!status && types[curIndex.value].label === typeLabel) {
        const firstActiveIndex = timeOptions.findIndex(time => time.active)
        if (firstActiveIndex !== -1) {
          curTimeIndex.value = firstActiveIndex
          return false
        }
      }

      // Update type active status based on its time options
      types.forEach(type => {
        type.active = times[type.label].some(time => time.active)
      })

      // Handle case when all time options are deactivated
      const currentType = types[curIndex.value]
      const isCurrentTypeEmpty = times[currentType.label].every(time => !time.active)
      
      if (isCurrentTypeEmpty) {
        currentType.active = status
        // Switch to first type that has active time options
        const firstActiveTypeIndex = types.findIndex(type => times[type.label].some(time => time.active))
        if (firstActiveTypeIndex !== -1) {
          changeType(firstActiveTypeIndex)
        }
      }
    }

    onMounted(() => {
      options.value = getOptions()

      // 此处暂时取巧，为了能获取初始为display: none的outcome宽度，计算conflict loading时间，从而避免多层props传递
      nextTick(() => {
        const { types, times } = options.value
        types.forEach((x) => {
          times[x.label].forEach((_time, index) => {
            options.value.times[x.label][index].visible = false
          })
        })
      })
    })

    return {
      event,
      curIndex,
      curTimeIndex,
      options,
      marketList,
      comboMarketGroups,
      changeType,
      changeTime,
      getType,
      checkDeactive
    }
  }
}
</script>

<style lang="scss" scoped>
.combo-market-2 {
  .combo-market-select {
    display: flex;
  }
}
</style>
